import { HttpClient, HttpEvent } from '@angular/common/http';
import {  Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { HubConnection } from '@microsoft/signalr';
import { Store } from '@ngrx/store';
import { combineLatest, from, interval, Observable, of, Subject, timer, BehaviorSubject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { currentOrganization, currentUser } from '../auth';
import { UpdateAvailabilitySuccess } from '../auth/auth.actions';
import { AppState } from '../core.state';
import { ActivitiesResponse, ActivityResponse, LanguageDetectResponse, Response, TrainingWebpage, User, VisitResponse } from '../../models';
import { Message, MessageResponse, Messages, MessagesResponse } from '../../models/message.model';
import { Visitor, VisitorResponse, Visitors } from '../../models/visitor.model';
import * as ActivityActions from '../../inbox/store/activity.actions';
import { takeUntil, filter, take, takeWhile, catchError } from 'rxjs/operators';
import { Title } from '@angular/platform-browser';
import { AllVisitorsRequested, VisitorTyping } from '../../inbox/store/visitors.actions';
import * as ToastrActions  from '../store/toastr.actions';
import * as TicketsActions from '../../inbox/store/tickets.actions';
import * as VisitorActions from '../../inbox/store/visitors.actions';
import * as PropertyActions from '../../core/store/properties.actions';


import { Update } from '@ngrx/entity';
import { HubDisconnected } from '../store/layout.actions';
import { selectVisitorById } from '../../inbox/store/visitors.selectors';
import { AllSettingsRequested } from '../../settings/store/settings.actions';
import { SavedRepliesRequested } from '../store/saved.replies.actions';
import { ChatFlowDeleteRequested, ChatFlowsRequested } from '../store/chatflow.actions';


const API_URL = environment.apiUrl;

@Injectable({
	providedIn: 'root'
})
export class ConversationsService {

	private hub: HubConnection | undefined;
	notify_sound: boolean = false;
	notify_popup: boolean = false;
	notify_flash: boolean = false;
	keep_ringing: boolean = false;
	agent_id: string;
	audio: HTMLAudioElement;

	// Flow execution logs observable
	private newFlowExecutionLogSubject = new Subject<any>();
	public newFlowExecutionLog$ = this.newFlowExecutionLogSubject.asObservable();

	sounds = ['/assets/media/new-message.wav',
		'/assets/media/sound-2.mp3',
		'/assets/media/sound-3.mp3',
		'/assets/media/sound-4.mp3',
		'/assets/media/sound-5.mp3',
		'/assets/media/sound-6.mp3',
	];

	constructor(
		private http: HttpClient,
		private store: Store<AppState>,
		private titleService: Title
	) {
		this.startHub();

		this.store.select(currentUser).pipe(
				filter(u => u !== undefined )
			).subscribe(u => {

				this.notify_sound = u.notify_sound;
				this.notify_popup = u.notify_popup;
				this.notify_flash = u.notify_flash;
				this.agent_id = u.id;
				this.keep_ringing = u.keep_ringing;
				this.audio = new Audio(this.sounds[u.sound - 1]);
			});


	}



	// @todo Implement email session
	public emailSession(session_id: string, visitor_id, recipient: string = "agent"): Observable < MessageResponse > {
		return this.http.put < MessageResponse > (`${API_URL}/api/activities/emailsession`, ({
			session_id: session_id,
			visitor_id: visitor_id,
			recipient: recipient
		}));
	}

	public reengageChatbot(visitor_id: string): Observable<Response> {
		return this.http.post<Response>(`${API_URL}/api/activities/reengagechatbot?visitor_id=${visitor_id}`, {});
	}

	public closeSession(visitor_id: string, notify_user: boolean, request_feedback: boolean): Observable < VisitorResponse > {
		return this.http.put < VisitorResponse > (`${API_URL}/api/activities/closesession?notify_user=${notify_user}&request_feedback=${request_feedback}`, ({
			id: visitor_id
		}));
	}

	public deleteSessions(visitor_id: string): Observable < VisitorResponse > {
		return this.http.put < VisitorResponse > (`${API_URL}/api/activities/deletesessions`, ({
			id: visitor_id
		}));
	}

	public getMessage(message_id: string): Observable < MessageResponse > {
		return this.http.get < MessageResponse > (`${API_URL}/api/messages/${message_id}`);
	}

	public getMessages(visitor_id: string): Observable < Messages > {
		return this.http.get < Messages > (`${API_URL}/api/messages?visitor=${visitor_id}`);
	}

	public getVisitor(visitor_id: string): Observable < VisitorResponse > {
		return this.http.get < VisitorResponse > (`${API_URL}/api/visitors/${visitor_id}`);
	}

	public getVisitors(property_id: string, view_mode: string | number, agent_id = '', page: number): Observable < Visitors > {
		
		let is_closed = (view_mode == 'closed') ? true : false;
		let agent = (view_mode == 'agent') ? agent_id : ''; 
		let attended_mode = (view_mode == 'unattended') ? 2 : 0;
		
		return this.http.get<Visitors>(
				`${API_URL}/api/visitors?property_ids=${property_id}
				&agent_id=${agent}
				&is_closed=${is_closed}
				&page=${page}
				&limit=${environment.recent_conversations_pagesize}
				&attended_mode=${attended_mode}
				&custom_view=${view_mode}`
			);
	}

	public searchVisitors(property_id: string, search: string, page: number): Observable<Visitors> {
		return this.http.get<Visitors>(
			`${API_URL}/api/visitors/search?property_ids=${property_id}&search=${encodeURIComponent(search)}&page=${page}&limit=${environment.recent_conversations_pagesize}`
		);
	}

	public visitorExistsinView(visitor_id: string, property_id:string, view_mode: string | number, agent_id = ''): Observable < Visitor > {

		let is_closed = (view_mode == 'closed') ? true : false;
		let agent = (view_mode == 'agent') ? agent_id : ''; 
		let attended_mode = (view_mode == 'unattended') ? 2 : 0;
		agent_id = agent_id || '';

		return this.http.get < Visitor > (
			`${API_URL}/api/visitors/${visitor_id}/exists-in-view?property_ids=${property_id}
				&agent_id=${agent}
				&is_closed=${is_closed}
				&attended_mode=${attended_mode}
				&custom_view=${view_mode.toString()}`
			);

	}

	public getVisit(activity_id: number): Observable < VisitResponse > {
		return this.http.get < VisitResponse > (`${API_URL}/api/visits/${activity_id}`);
	}

	public getActivities(visitor_id: string): Observable < ActivitiesResponse > {
		return this.http.get < ActivitiesResponse > (`${API_URL}/api/activities?visitor=${visitor_id}`);
	}

	public saveVisitorInfo(id: string | number, visitor: Partial < Visitor > ): Observable < VisitorResponse > {
		return this.http.put < VisitorResponse > (`${API_URL}/api/visitors/${id}`, visitor);
	}

	public uploadAttachment(file: FormData): Observable<HttpEvent<MessageResponse>> {
		return this.http.post<MessageResponse>(`${API_URL}/api/messages/uploadfile`, file, { reportProgress: true, observe: 'events' }); 
	}

	public detectLanguage(visitor_id: string): Observable < LanguageDetectResponse > {
		return this.http.get < LanguageDetectResponse > (`${API_URL}/api/translate/detect?visitor_id=${visitor_id}`);
	}

	public translateLastSession(visitor_id: string): Observable <MessagesResponse> {
		return this.http.post<MessagesResponse>(`${API_URL}/api/translate/translate?visitor_id=${visitor_id}`, {});
	}

	public reassignSession(visitor_id: string, agent_id: string): Observable<VisitorResponse> {
		return this.http.put<VisitorResponse>(`${API_URL}/api/visitors/${visitor_id}/reassign?agent_id=${agent_id}`, {});
	}

	send(message: Message) {
		if (this.hub) {
			this.hub.invoke('SendtoVisitor', message);
		}
		return of(message);
	}

	joinGroup(group: string): void {
		if (this.hub) {
			this.hub.invoke('JoinGroup', group);
		}
	}

	leaveGroup(group: string): void {
		if (this.hub) {
			this.hub.invoke('LeaveGroup', group);
		}
	}

	getAllGroups(): Observable < string[] > {
		//return this.http.get<string[]>(this.actionUrl, { headers: this.headers });
		return of([]);
	}

	ping(): Observable<string> {
		
		if (!this.hub) return of('failed');

		return from(this.hub.invoke<string>('Ping')).pipe(
			catchError(err => {
				return of('failed'); // fallback so observable continues
			})
		);
	}

	public startHub() {

		this.hub = this.getNewHub()

		this.hub.start()
			.then(() => {
				console.log("Connection Established");

				this.store.select(currentOrganization).subscribe(org => {
					if (org == null) return;

					console.log("Registering Agent...");
					this.hub.invoke("AgentConnected", org.id)
				});

			})
			.catch(err =>
				console.info(err.toString())
			);

		this.registerHandlers();

	}

	reconnect(){

		console.info("Current hub state ➡️"	, this.hub?.state);
		this.hub.start()
			.then(() => {
				console.info("Connection Restablished using existing Hub");
				this.store.dispatch(HubDisconnected({is_disconnected: false}));
				this.registerHandlers();
				this.refreshSettings();
			})
			.catch(err => {
				console.info(err.toString());

				// starting on the hub failed, lets try to start a new connection
				try {
					console.info("Stopping existing Hub");
					this.hub.stop();
				} catch (err) {
					console.info("Error stopping existing hub:", err);
				}
				this.hub = this.getNewHub();
				this.hub.start().then(() => {
					console.info("New Hub Created - Connection Restablished");
					this.store.dispatch(HubDisconnected({is_disconnected: false}));
					this.registerHandlers();
					this.refreshSettings();

				})
				.catch(
					err => console.info(err.toString())
				);
		});
	}


	private getNewHub(){

		return new signalR.HubConnectionBuilder()
			.configureLogging(signalR.LogLevel.Information)
			.withAutomaticReconnect()
			.withStatefulReconnect()
			.withUrl(`${environment.apiUrl}/hub`)
			.build();
	}

	private refreshSettings(){

		this.store.select(currentOrganization).subscribe(org => {
			if (org == null) return;

			this.hub.invoke("AgentConnected", org.id)

			this.store.dispatch(SavedRepliesRequested({organization_id: org.id}));
			this.store.dispatch(ChatFlowsRequested({organization_id: org.id}));
			this.store.dispatch(AllVisitorsRequested({paging: {page: 0}}));
			this.store.dispatch(AllSettingsRequested());

		});
	}

	private registerHandlers() {

		this.hub.on("ackMessage", (id: string) => {
			this.store.dispatch(ActivityActions.MessageActivityReceived({
				message_id: id
			}));
			console.log('Acknowledge recieved: ' + id);
		});

		this.hub.on("SendtoVisitorFailed", (message: string, message_id: string) => {
			this.store.dispatch(ActivityActions.SendMessageFailed({
				error: message, message_id: message_id
			}));
		});

		this.hub.on("messageReceived", (id: string) => {
			console.log('Message Activity received: ' + id);
			this.store.dispatch(ActivityActions.MessageActivityReceived({
				message_id: id
			}));
		});

		this.hub.on("visitReceived", (id: number) => {
			this.store.dispatch(ActivityActions.VisitRequested({
				id: id
			}));
		});

		this.hub.on("visitorOffline", (visitor_id: string) => {
			console.log("Visitor went offline: " + visitor_id);
			this.store.dispatch(VisitorActions.VisitorOfflineReceived({
				visitor_id: visitor_id
			}));
		});

		this.hub.on("visitorOnline", (visitor_id: string) => {
			console.log("Visitor went online: " + visitor_id);
			this.store.dispatch(VisitorActions.VisitorOnlineReceived({
				visitor_id: visitor_id
			}));
		});

		this.hub.on("newTicket", (id: number) => {
			this.store.dispatch(TicketsActions.NewTicketReceived({
				ticket_id: id
			}));
		});

		this.hub.on("newTicketReply", (id: number) => {
			this.store.dispatch(TicketsActions.NewReplyReceived({
				ticket_id: id
			}));
		});

		this.hub.on("SessionReassigned", (payload: { visitor_id: string; assigner_name: string; assignee_id: string }) => {
			this.store.dispatch(VisitorActions.ReassignSessionUpdateReceived(payload));
		});

		this.hub.on("newSession", (id: string, created_at: string, visitor_id: string) => {
			this.store.dispatch(
				ActivityActions.NewSession({
					id: id,
					created_at: created_at,
					visitor_id: visitor_id
				})
			);
		});

		this.hub.on("messageFromAgent", (id: string, agent: string) => {
				
			this.store.dispatch(ActivityActions.AgentActivityReceived({
				message_id: id
			}));
		});

		this.hub.on("VisitorTyping", (visitor_id: string, is_typing: boolean) => {
			this.store.dispatch(VisitorTyping({
				visitor_id: visitor_id,
				is_typing: is_typing
			}))
		});

		this.hub.on("documentTrainingStatusUpdate", (document_id: number, property_id: number, status: number) => {
			this.store.dispatch(PropertyActions.SetDocumentTrainingStatus({
				document_id: document_id,
				property_id: property_id,
				status: status
			}))
		});

		this.hub.on("updateWebpagesStatus", (webpages: TrainingWebpage[], property_id: number) => {
			this.store.dispatch(PropertyActions.SetWebpagesTrainingStatus({
				webpages: webpages,
				property_id: property_id
			}))
		});

		this.hub.on("MessageReadByVisitor", (message_id: string ) => {
			this.store.dispatch(ActivityActions.MessageReadByVisitorReceived({
				message_id: message_id
			}))
		});

		this.hub.on("MessageReadByVisitorMany", (message_ids: string[] ) => {
			message_ids.forEach( message_id => {
				this.store.dispatch(ActivityActions.MessageReadByVisitorReceived({
					message_id: message_id
				}))
			});
		});

		this.hub.on("AcknowledgeReceive", (message_id: string ) => {
			this.store.dispatch(ActivityActions.MessageReceivedByVisitor({
				message_id: message_id
			}))
		});

		this.hub.on("AcknowledgeReceiveMany", (message_ids: string[] ) => {
			message_ids.forEach( message_id => {
				this.store.dispatch(ActivityActions.MessageReceivedByVisitor({
					message_id: message_id
				}))
			});
		});

		this.hub.on("updateVisitorAttributes", (visitor: Visitor) => {
			console.log(`Updated Visitor - ${visitor.id}`);
		
			this.store.dispatch(VisitorActions.VisitorAttributeUpdate({visitor_id: visitor.id}));
		
		});

		this.hub.on("newFlowExecutionLog", (log: any) => {
			console.log('New Flow Execution Log received:', log);
			this.newFlowExecutionLogSubject.next(log);
		});

		this.hub.on("agentAvailabilityChanged", (agent_id: string, availability: number) => {
			console.log(`Agent availability changed: ${agent_id} - ${availability}`);
			
			// Only update if it's the current user
			if (agent_id === this.agent_id) {
				this.store.dispatch(UpdateAvailabilitySuccess({ availability: availability }));
			}
		});

		this.hub.onclose(error => {
			this.store.dispatch(HubDisconnected({is_disconnected: true}));
		});

		this.hub.onreconnecting(error => {
			this.store.dispatch(HubDisconnected({is_disconnected: true}));
		});

		this.hub.onreconnected(error => {
			this.store.dispatch(HubDisconnected({is_disconnected: false}));
		});
	}

	show_notification( message: string, title: string, force: boolean = false ) {

		if ( (!force) && (!this.notify_popup) ) return;

		if (Notification.permission === "granted") {

			let notification = new Notification(title, {body: message });

		} else if (Notification.permission === "default") {

			Notification.requestPermission(function (permission) {
				// If the user accepts, let's create a notification
				if (permission === "granted") {
					let notification = new Notification(title, {body: message });
				}
			});
		}

	}

	play_notification(force: boolean = false){

		if ( (!force) && (!this.notify_sound) ) return;

		this.audio.play();

	}

	loop_sound(visitor: Visitor){

		if (!this.keep_ringing) return;

		combineLatest([
			interval(6000),
			this.store.select(selectVisitorById(visitor.id))
		]).pipe(
			takeWhile( ([counter, visitor]) => visitor != null && visitor.is_attended == false && this.keep_ringing)
		).subscribe(([counter, visitor]) => {
			this.play_notification();
		});

	}

	flash_notification(message: string = "New Message", force: boolean = false){

		if ( (!force) && (!this.notify_flash) ) return;

		var original_title = this.titleService.getTitle();
		timer(0, 1000).pipe(take(4)).subscribe(s => {

			if (s % 2 == 0) {
				// 0, 2
				this.titleService.setTitle(message);
			} else {
				// 1, 3
				this.titleService.setTitle(original_title);
			}

		});

	}

}
